import { AttachFile, ErrorOutline, UploadFileOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { useMutation } from '@tanstack/react-query'
import { MuiFileInput } from 'mui-file-input'
import React, { FunctionComponent, useState } from 'react'

import { useError } from '../../../../base/contexts/ErrorProvider.tsx'
import { useToast } from '../../../../base/contexts/ToastContext.tsx'
import { ArticuloProps } from '../../../../interfaces/articulo.ts'
import { alphaByTheme } from '../../../../utils/colorUtils.ts'
import { swalClose, swalLoading } from '../../../../utils/swal'
import { apiArticuloImportarImagen } from '../../api/apiArticuloImportarImagen'

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  row: ArticuloProps
  onClose: (value?: boolean) => void
}

type Props = OwnProps

/**
 * Modulo de importación o actualización de imagenes
 * @param props
 * @constructor
 */
const ArticuloImportarImagenDialog: FunctionComponent<Props> = (props) => {
  const { row, onClose, open, ...other } = props
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)

  const { showError } = useError()
  const { toast } = useToast()

  const { mutate, data, isPending, isError, error, reset } = useMutation({
    mutationFn: (fileToUpload: File) => apiArticuloImportarImagen(row.codigoArticulo, '57', fileToUpload),
    // 1. Justo antes de que empiece la mutación
    onMutate: () => {
      swalLoading('Subiendo imagen...')
    },
    // 2. Cuando la mutación termina (ya sea éxito o error)
    onSettled: () => {
      swalClose()
    },
    onSuccess: (res) => {
      if (res) {
        setPreviewUrl(res.variants.medium)
        setFile(null)
      }
    },
    onError: (err) => {
      showError(err)
    },
  })

  /** Evento onchange cuando selecciona el archivo */
  const handleFileChange = (selectedFile: File | null) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']

    // Si había una previa de blob anterior, la limpiamos
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl)
    }

    if (selectedFile) {
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Formato no permitido. Use JPG, PNG, WEBP, GIF o SVG')
        setFile(null)
        return
      }

      if (selectedFile.size > 200000) {
        // 200kb
        toast.warning('La imagen no puede ser mayor a 200 kb')
        return
      }

      setFile(selectedFile)
      setPreviewUrl(URL.createObjectURL(selectedFile)) // Crear preview local
      reset()
    } else {
      setFile(null)
      // Volvemos a la imagen original si cancela la selección
      setPreviewUrl(row?.imagen?.variants?.medium || null)
    }
  }

  /**
   * Envia el archivo al servidor
   */
  const handleUpload = () => {
    if (file) mutate(file)
  }

  // Sincronización al abrir el diálogo (Solución al error de ESLint)
  const handleTransitionEnter = () => {
    if (row?.imagen?.variants?.medium) {
      setPreviewUrl(row.imagen.variants.medium)
    } else {
      setPreviewUrl(null)
    }
    setFile(null)
    reset()
  }

  // Limpieza al cerrar
  const handleTransitionExited = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl) // Liberar memoria
    }
    setPreviewUrl(null)
    setFile(null)
    reset()
  }

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      disableEnforceFocus
      disableAutoFocus
      slotProps={{
        transition: { onEnter: handleTransitionEnter, onExited: handleTransitionExited },
      }}
      {...other}
    >
      <DialogTitle>
        Imagen del articulo <b>{row.codigoArticulo}</b>{' '}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Paper
            variant="outlined"
            sx={(theme) => ({
              width: '100%',
              height: 250,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alphaByTheme(grey[500], theme),
              overflow: 'hidden',
            })}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Typography color="text.secondary">Sin imagen disponible</Typography>
            )}
          </Paper>

          {file && (
            <Alert severity="info" sx={{ width: '100%' }}>
              Nueva imagen seleccionada lista para importar. Clic en "Importar Imagen"
            </Alert>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid size={12}>
            <MuiFileInput
              size={'medium'}
              fullWidth
              sx={{ mt: 3, mb: 0.5 }}
              label={'Seleccione su archivo de imagen'}
              value={file}
              slotProps={{
                htmlInput: {
                  accept: 'image/jpeg, image/png, image/webp, image/gif, image/svg+xml',
                },
                input: {
                  startAdornment: <AttachFile />,
                },
              }}
              placeholder="Clic aqui para seleccionar su imagen"
              clearIconButtonProps={{
                title: 'Quitar',
                children: <CloseIcon fontSize="small" />,
                onClick: () => handleFileChange(null),
              }}
              onChange={handleFileChange}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Formatos: PNG, JPG, WEBP. Tamaño máx: 200 KB.
            </Typography>
          </Grid>
          <Grid size={12}>
            {isError && (
              <Alert severity="error" icon={<ErrorOutline />}>
                Error: {(error as Error).message}
              </Alert>
            )}
            {data?.id && <Alert severity="success">Imagen subida: {data.filename}</Alert>}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button
          color={'error'}
          disabled={isPending}
          onClick={() => {
            onClose()
          }}
        >
          Cerrar
        </Button>
        <LoadingButton
          color={'primary'}
          loading={isPending}
          variant={'contained'}
          startIcon={<UploadFileOutlined />}
          onClick={handleUpload}
          disabled={!file}
        >
          {data ? 'Subido' : 'Importar Imagen'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ArticuloImportarImagenDialog
